import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import React, { useMemo, useState } from 'react';
import { WidgetItemLiveChannel } from '../../../../apiService';
import { Card, CardBody, CardImage, Flex, Icon, Text } from '../../../../styles';
import { getWidgetsTeaserPath, inspectFlags, useResponsiveUrl, usePlayerService } from '../../../../utils';
import { __t } from '../../../../i18n';
import moment from 'moment-jalaali';
import IconSchedule from '../../../../icons/IconScheduleTable';
import CardBadge from '../../../../CardBadge';
import { IconLock, IconPlay, IconVolumeOff, IconVolumeUp } from '../../../../icons';
import HlsPlayer from '../WidgetsPlayer/LazyHlsPlayer';
import { Controls, ControlButton } from '../WidgetsPlayer/UI';
import IconLiveStreaming from '../../../../icons/IconLiveStreaming';
import useDelayedHover from '../../../../useDelayedHover';
import usePauseHeroVideo from '../../../../utils/usePauseHeroVideo';

moment.loadPersian({ dialect: 'persian-modern' });

const DESKTOP_MIN_WIDTH = 1200;

export default function LiveCarouselCard({ item }: { item: WidgetItemLiveChannel }) {
  const { conductor } = item;
  const { video_content, title, is_live, start_stream_time } = conductor;

  const { playable, hasAccess, ageRestricted, isForeignIP, free, hasSpecificCarrie, blocked } = inspectFlags([video_content?.conditional_flag, video_content?.flag].join(', ') || '');
  const accessRestricted = isForeignIP;

  const imgUrl = useResponsiveUrl({
    url: video_content?.cover_image?.path || video_content?.poster_image?.path,
    desktop: 500,
    mobile: 250,
  });

  const cardTitle = title || video_content?.title;
  // Standard content badges (free / coming soon / cinema, etc.), same as ContentCard. The "free"
  // flag (traffic_free) lives in conditional_flag, so both flag strings are passed.
  const badgeFlags = `${video_content?.flag ?? ''}, ${video_content?.conditional_flag ?? ''}`;
  const [handlePlay] = usePlayerService({ id: video_content?.short_id || '', playable, hasAccess, free, hasSpecificCarrie });
  // Live & watchable -> player; needs login -> login (handled by usePlayerService). No live content -> nothing.
  const handleClick = () => {
    if (!playable || accessRestricted || ageRestricted || blocked) return;
    if (is_live && video_content?.short_id) handlePlay('l');
  };

  // Memoized so the reference stays stable across re-renders (e.g. from usePlayerService),
  // otherwise HlsPlayer would re-initialize on every render and the preview never plays.
  const liveSource = useMemo(
    () => getWidgetsTeaserPath(video_content?.video_files, video_content?.id || ''),
    [video_content?.video_files, video_content?.id],
  );
  const restricted = accessRestricted || ageRestricted || blocked;
  const previewable = is_live && !restricted && liveSource?.type === 'application/x-mpegURL' && !!liveSource?.src;

  const [hovered, setHovered] = useState(false);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const { pauseHero, resumeHero } = usePauseHeroVideo();

  const delayedHover = useDelayedHover((isHovered) => {
    setHovered(isHovered);
    setPreviewPlaying(isHovered && !!previewable);
  });

  const onMouseEnter = () => {
    if ((typeof window !== 'undefined' && window.innerWidth <= DESKTOP_MIN_WIDTH) || restricted) return;
    // Pause the page's teaser video while the preview plays (like EpisodeItem).
    pauseHero();
    delayedHover(true);
  };

  const onMouseLeave = () => {
    delayedHover(false);
    resumeHero();
  };

  return (
    <CardCell>
      {/* Only this element scales on hover — the title below stays its normal size. */}
      <CardScale active={hovered && !restricted} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        <Card orientation="horizontal" block onClick={handleClick} style={{ cursor: is_live ? 'pointer' : 'default' }}>
          <CardImage src={imgUrl} alt={cardTitle} loading="lazy" />

          {previewPlaying && liveSource?.src && (
            <HlsPlayer source={liveSource} active={hovered} live playLimit={20} autoplay onEnded={() => setPreviewPlaying(false)}>
              {(api) => (
                <Controls data-player-controls="true" splitControls={false}>
                  <ControlButton type="button" aria-label="Play">
                    <Icon size="semiSmall" cursor='pointer' ml='0.4em'>
                      <IconPlay />
                    </Icon>
                  </ControlButton>
                  <ControlButton
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.currentTarget.blur();
                      api.toggleMute();
                    }}
                    aria-label={api.muted ? 'Unmute' : 'Mute'}
                  >
                    {api.muted ? <IconVolumeOff /> : <IconVolumeUp />}
                  </ControlButton>
                </Controls>
              )}
            </HlsPlayer>
          )}

          {ageRestricted && (
            <MessageBg>
              <>
                <IconLock />
                <Text variant="body2" my="0.5rem">
                  {__t('live.age_restricted')}
                </Text>
              </>
            </MessageBg>
          )}

          {accessRestricted && (
            <MessageBg>
              <>
                <IconLock />
                <Text variant="body2" my="0.5rem">
                  {__t('live.ip_restricted')}
                </Text>
              </>
            </MessageBg>
          )}

          {blocked && video_content?.conditional_flag_description && (
            <MessageBg>
              <>
                <IconLock />
                <Text variant="body2" my="0.5rem">
                  {video_content?.conditional_flag_description}
                </Text>
              </>
            </MessageBg>
          )}

          <Badges $hidden={hovered && !restricted}>
            <CardBadge flag={badgeFlags} />
            {!is_live && (
              <Flex as={CardBody} align="center" justify="center">
                <IconSchedule />
              </Flex>
            )}
            <BadgeWrapper position="right-bottom">
              {is_live ? (
                <PlayingBadge align="center">
                  <BlinkingIcon size="large">
                    <IconLiveStreaming />
                  </BlinkingIcon>
                  <Text variant="body2" weight="bold">
                    {__t('common.live')}
                  </Text>
                </PlayingBadge>
              ) : (
                <Schedule streamTime={start_stream_time} />
              )}
            </BadgeWrapper>
          </Badges>

          {/* Not live: no video preview on hover, so surface the title centered over the dimmed
              image. Inside the card so it tracks the scale cleanly — no corner drift. */}
          {!is_live && (
            <HoverTitleOverlay $visible={hovered && !restricted}>
              <HoverTitle variant="body2" weight="bold">
                {cardTitle}
              </HoverTitle>
            </HoverTitleOverlay>
          )}
        </Card>
      </CardScale>

      {/* Title sits outside CardScale, so it keeps its size; it just fades out while hovering. */}
      <CardTitle $hidden={hovered && !restricted} variant="body2">
        {cardTitle}
      </CardTitle>
    </CardCell>
  );
}

// The grid cell — never scales, so it keeps its layout slot while the card inside grows.
const CardCell = styled.div(() => ({
  position: 'relative',
}));

// Smooth CSS hover-scale, same as VitrineCard. The carousel's Scroller uses extra padding to
// accommodate the scale without clipping (hoverOverflowVisibleOnDesktop).
const CardScale = styled.div<{ active: boolean }>(({ active }) => ({
  position: 'relative',
  zIndex: 1,
  transition: 'transform 550ms ease',
  transformOrigin: 'center',
  ...(active && {
    transform: 'scale(1.25)',
    zIndex: 9999,
    '& video': {
      opacity: 1,
    },
  }),
}));

// Under-card title — outside the scaling element, fades out while the card is scaled up.
const CardTitle = styled(Text)<{ $hidden?: boolean }>(({ theme, $hidden }) => ({
  display: 'block',
  maxWidth: '100%',
  marginTop: '0.5rem',
  marginBottom: '0.5rem',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: theme.palette.foreground[900][0],
  transition: 'opacity 240ms ease',
  opacity: $hidden ? 0 : 1,
}));

const Badges = styled.div<{ $hidden?: boolean }>(({ $hidden }) => ({
  transition: 'opacity 240ms ease',
  opacity: $hidden ? 0 : 1,
  pointerEvents: $hidden ? 'none' : undefined,
}));

// Live icon pulses between white and the theme's main color. CSS `fill` overrides the SVG's
// inline `fill="#fff"`, so the underlying icon component stays untouched.
const blink = (color: string) => keyframes({
  '0%, 100%': { fill: '#fff' },
  '50%': { fill: color },
});

const BlinkingIcon = styled(Icon)(({ theme }) => ({
  '& svg g': {
    animation: `${blink(theme.palette.primary[900][0])} 1.2s ease-in-out infinite`,
  },
}));

// Glassy "playing" label — same translucent, blurred background as the player's live badge.
const PlayingBadge = styled(Flex)(({ theme }) => ({
  alignItems: 'center',
  gap: '0.25em',
  padding: '0.25em 1em',
  borderRadius: theme.radius.pill,
  background: theme.rgba(theme.palette.foreground[800][1], 0.7),
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
  color: theme.palette.text[700][0],
}));

// Non-live hover: a dimmed overlay filling the card, with the title centered. Fills the card so it
// scales together with it — no corner anchoring, so nothing drifts on hover.
const HoverTitleOverlay = styled.div<{ $visible?: boolean }>(({ theme, $visible }) => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1),
  background: theme.rgba(theme.palette.background[800][0], 0.55),
  transition: 'opacity 240ms ease',
  opacity: $visible ? 1 : 0,
  pointerEvents: 'none',
}));

const HoverTitle = styled(Text)(({ theme }) => ({
  textAlign: 'center',
  color: theme.palette.text[700][0],
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}));

const BadgeWrapper = styled.div(({ position }: { position: string }) => {
  const positions = position === 'left-bottom' ? { bottom: -1, left: -1 } : { bottom: 8, right: 8 };

  return {
    position: 'absolute',
    zIndex: 1,
    pointerEvents: 'none',
    ...positions,
  };
});

const Schedule = ({ streamTime }: { streamTime: string }) => {
  const date = moment(streamTime);
  const daysToLive = date.diff(moment(), 'days');
  return (
    <PlayingBadge>
      <Text variant="body2">
        {daysToLive >= 7
          ? date.format('jD jMMMM')
          : (moment().diff(date, 'days') >= 2) ? date.fromNow() : date.calendar().split(' ')[0]}
        <span style={{ marginInline: '4px', display: 'inline-block' }}>-</span>
        {moment(date).format('HH:mm')}
      </Text>
    </PlayingBadge>
  );
};

const MessageBg = styled.div(({ theme }) => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  top: 0,
  left: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.rgba(theme.palette.background[800][0], .8),
  padding: theme.spacing(1),
}));

// const RestrictIpPill = styled(Tag)(({ theme }) => ({
//   position: 'absolute',
//   top: '50%',
//   left: '50%',
//   transform: 'translate(-50%, -50%) scale(1.05)',
//   background: theme.palette.success[900][0],
// }));
